import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import ArticleHero from './components/ArticleHero';
import ArticleContent from './components/ArticleContent';
import Footer from '@/pages/home/components/Footer';
import ArticleSchema from '@/components/feature/ArticleSchema';

function ArticleNav() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`relative z-50 w-full transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/60 ${scrolled ? 'fixed top-0' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-[72px]">
        <Link
          to="/"
          className="text-xl md:text-2xl font-heading font-bold text-foreground-900 whitespace-nowrap"
        >
          hipobuyspread<span className="text-accent-400">.org</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div ref={guidesRef} className="relative">
            <button
              onClick={() => setGuidesDropdownOpen(!guidesDropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Guides
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {guidesDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 z-50 max-h-80 overflow-y-auto">
                <Link to="/hipobuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-primary-700 bg-primary-50 font-medium whitespace-nowrap" onClick={() => setGuidesDropdownOpen(false)}>
                  Hipobuy Spreadsheet Guide
                </Link>
                <Link to="/kakobuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => setGuidesDropdownOpen(false)}>
                  Kakobuy Spreadsheet Guide
                </Link>
                <Link to="/cssbuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => setGuidesDropdownOpen(false)}>
                  CSSBuy Spreadsheet Guide
                </Link>
                <Link to="/superbuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => setGuidesDropdownOpen(false)}>
                  Superbuy Spreadsheet Guide
                </Link>
                <Link to="/oopbuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => setGuidesDropdownOpen(false)}>
                  Oopbuy Spreadsheet Guide
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            Hipobuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900"
        >
          <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <Link to="/" className="flex items-center gap-3 py-3 text-foreground-700 font-semibold text-sm" onClick={() => setMobileMenuOpen(false)}>
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50"><i className="ri-home-4-line text-primary-600"></i></span>
            Home
          </Link>

          <div>
            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="flex items-center justify-between w-full py-3 text-foreground-700 font-semibold text-sm"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50"><i className="ri-article-line text-accent-600"></i></span>
                SEO Guides
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-foreground-400 ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileGuidesOpen && (
              <div className="ml-11 space-y-1 pb-2">
                <Link to="/hipobuy-spreadsheet-guide" className="flex items-center gap-2 py-2.5 px-3 text-sm text-primary-700 bg-primary-50 rounded-md font-medium" onClick={() => setMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                  Hipobuy Spreadsheet Guide
                </Link>
                <Link to="/kakobuy-spreadsheet-guide" className="flex items-center gap-2 py-2.5 px-3 text-sm text-foreground-600 hover:text-primary-700 rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                  Kakobuy Spreadsheet Guide
                </Link>
                <Link to="/cssbuy-spreadsheet-guide" className="flex items-center gap-2 py-2.5 px-3 text-sm text-foreground-600 hover:text-primary-700 rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 flex-shrink-0"></span>
                  CSSBuy Spreadsheet Guide
                </Link>
                <Link to="/superbuy-spreadsheet-guide" className="flex items-center gap-2 py-2.5 px-3 text-sm text-foreground-600 hover:text-primary-700 rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-300 flex-shrink-0"></span>
                  Superbuy Spreadsheet Guide
                </Link>
                <Link to="/oopbuy-spreadsheet-guide" className="flex items-center gap-2 py-2.5 px-3 text-sm text-foreground-600 hover:text-primary-700 rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-300 flex-shrink-0"></span>
                  Oopbuy Spreadsheet Guide
                </Link>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] text-foreground-400 font-semibold uppercase tracking-widest px-1 pb-3">Shop by Category</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 py-2.5 px-2.5 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors bg-background-100/70"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded bg-background-200/60">
                    <i className="ri-arrow-right-up-line text-[10px] text-foreground-400"></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-2 pt-3 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold"
            >
              <i className="ri-shopping-cart-line"></i>
              Buy It Now
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3 bg-background-100 border border-background-200/60 text-foreground-700 rounded-full text-sm font-medium"
            >
              <i className="ri-external-link-line"></i>
              Hipobuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export default function Article() {
  return (
    <main>
      <ArticleSchema
        slug="hipobuy-spreadsheet-guide"
        title="Hipobuy Spreadsheet Ultimate Guide 2026 - Complete Shopping Resource"
        description="The most comprehensive Hipobuy Spreadsheet guide covering navigation, category organization, platform comparisons, safety practices, and expert tips for all fourteen product categories."
        imageUrl="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20open%20notebook%20with%20handwritten%20shopping%20notes%20and%20spreadsheet%20printouts%20surrounded%20by%20fashion%20lookbook%20pages%2C%20warm%20desk%20lamp%20glow%2C%20organized%20creative%20workspace%20aesthetic%2C%20parchment%20and%20ivory%20tones%20with%20subtle%20sage%20green%20accents%2C%20intellectual%20cozy%20atmosphere%2C%20editorial%20planning%20scene&width=1920&height=800&seq=article-hero-v2-2026&orientation=landscape"
      />
      <ArticleNav />
      <ArticleHero />
      <ArticleContent />
      <Footer />
    </main>
  );
}